import React, { FC, useEffect, useMemo, useState } from 'react';
// eslint-disable-next-line import/no-extraneous-dependencies
import { Plugin } from 'prosemirror-state';

import { EditorContent, useEditor } from '@tiptap/react';
import { Editor } from '@tiptap/core';
import ImageResize from 'tiptap-extension-resize-image';
import ImageExt from '@tiptap/extension-image';
import LinkExt from '@tiptap/extension-link';
import StarterKit from '@tiptap/starter-kit';
import { FormikProps } from 'formik';
import { Box } from '@mui/system';

import useEditorUtility from '../../../hooks/Editor/useEditorUtility';

import EditorToolbar from './EditorToolbar';
import {
  deserializeEmailConditions,
  serializeEmailConditions,
} from './extensions/EmailCondition';
import styles from './styles';
import './style.css';

const extensions = [
  StarterKit,
  LinkExt.configure({
    openOnClick: false,
    autolink: true,
    linkOnPaste: true,
  }),
  ImageExt.configure({
    inline: true,
  }).extend({
    addProseMirrorPlugins() {
      return [
        new Plugin({
          props: {
            handleDOMEvents: {
              paste(view, event) {
                const hasFiles = event?.clipboardData?.files?.length;

                if (!hasFiles) {
                  return;
                }

                const images = Array.from(event.clipboardData.files).filter(
                  (file) =>
                    /image/i.test(file.type),
                );

                if (images.length === 0) {
                  return;
                }

                event.preventDefault();

                const { schema } = view.state;

                images.forEach((image) => {
                  const reader = new FileReader();

                  reader.onload = (readerEvent) => {
                    const node = schema.nodes.image.create({
                      src: readerEvent?.target?.result,
                    });
                    const transaction = view.state.tr.replaceSelectionWith(node);
                    view.dispatch(transaction);
                  };
                  reader.readAsDataURL(image);
                });
              },
            },
          },
        }),
      ];
    },
  }),
  ImageResize.configure({ inline: true }),
];

interface IEmailEditor {
  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  formik: FormikProps<any>;
  name: string;
  conditionTag?: string;
  conditionTagOptions?: Array<{ key: string; value: string }>;
  previousContent?: string;
  onEditorReady?: (editor: Editor | null) => void;
  placeholderPluginOptions?: Array<Record<string, string>>
}

const EmailEditor: FC<IEmailEditor> = ({
  formik,
  name,
  conditionTag,
  conditionTagOptions,
  onEditorReady,
  previousContent,
  placeholderPluginOptions,
}) => {
  const { handleDrop } = useEditorUtility();
  const [focus, setFocus] = useState(false);
  const conditionTags = useMemo(
    () =>
      [
        conditionTag,
        ...(conditionTagOptions?.map((option) =>
          option.key) ?? []),
      ],
    [conditionTag, conditionTagOptions],
  );
  const editor = useEditor({
    extensions,
    content: deserializeEmailConditions(formik.values[name], conditionTags),
    editorProps: {
      handleDrop,
    },
    onUpdate: ({ editor: currentEditor }) => {
      formik.setFieldValue(
        name,
        serializeEmailConditions(currentEditor.getHTML(), conditionTags),
      );
    },
  });

  useEffect(() => {
    onEditorReady?.(editor);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editor]);

  useEffect(() => {
    const nextContent = previousContent ?? formik.values[name] ?? '';
    if (
      editor
      && serializeEmailConditions(editor.getHTML(), conditionTags) !== nextContent
    ) {
      editor.commands.setContent(
        deserializeEmailConditions(nextContent, conditionTags),
      );
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conditionTags, editor, name, previousContent]);

  const handleFocus = (focused: boolean) =>
    () => {
      setFocus(focused);
    };

  return (
    <Box sx={[styles.active, focus && styles.focusedStyle]}>
      <EditorToolbar
        conditionTag={conditionTag}
        conditionTagOptions={conditionTagOptions}
        placeholderPluginOptions={placeholderPluginOptions}
        editor={editor}
      />
      <EditorContent
        style={styles.editor}
        editor={editor}
        rows={5}
        height={200}
        onFocus={handleFocus(true)}
        onBlur={handleFocus(false)}
      />
    </Box>
  );
};

export default EmailEditor;
